import { useState, useEffect } from 'react';
import { getGenerationData } from '../services/CustomerService';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ControlPointIcon from '@mui/icons-material/ControlPoint';
import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  useMediaQuery,
  Tooltip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  Alert,
  Stack,
  Skeleton,
} from '@mui/material';
import Logo from '../components/Logo';
import TableChartIcon from '@mui/icons-material/TableChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import MWETable from '../components/SolarGeneration/MWETable';
import MWEGraph from '../components/SolarGeneration/MWEGraph';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useLocation } from 'react-router-dom';

const SolarEstimationUI = () => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const theme = useTheme();
  const [view, setView] = useState('graph');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  const initialFormData = {
    // Required fields
    pin_code: queryParams.get('pin_code') || '110001',
    system_capacity_in_kw: parseFloat(
      queryParams.get('system_capacity_in_kw') || '3.39',
    ),
    tilt: parseFloat(queryParams.get('tilt') || '12.0'),
    // Optional fields - initialize as null
    lat: queryParams.get('lat') ? parseFloat(queryParams.get('lat')) : null,
    long: queryParams.get('long') ? parseFloat(queryParams.get('long')) : null,
    losses: queryParams.get('losses')
      ? parseFloat(queryParams.get('losses'))
      : null,
    soil_condition: queryParams.get('soil_condition') || null,
    inv_eff: queryParams.get('inv_eff')
      ? parseFloat(queryParams.get('inv_eff'))
      : null,
    angle_from_north: queryParams.get('angle_from_north')
      ? parseFloat(queryParams.get('angle_from_north'))
      : null,
  };

  const [formData, setFormData] = useState(initialFormData);

  const [loading, setLoading] = useState(false);
  const [apiData, setApiData] = useState(null);
  const [isSelectedPINCode, setIsSelectedPINCode] = useState(false);
  const [errors, setErrors] = useState({});

  const [fetchedCoords, setFetchedCoords] = useState(null);

  const mobileHeaderStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    py: 3,
  };

  const defaultHeaderStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    py: 3,
  };
  const validateField = (name, value) => {
    // Required fields must not be empty
    const requiredFields = ['pin_code', 'system_capacity_in_kw', 'tilt'];
    if (
      requiredFields.includes(name) &&
      (value === null || value === undefined || value === '')
    ) {
      return 'This field is required';
    }

    // Optional fields can be null or empty
    if (
      !requiredFields.includes(name) &&
      (value === null || value === undefined || value === '')
    ) {
      return '';
    }

    switch (name) {
      case 'system_capacity_in_kw':
        return parseFloat(value) > 0 ? '' : 'Capacity must be greater than 0';
      case 'lat':
        return parseFloat(value) >= -90 && parseFloat(value) <= 90
          ? ''
          : 'Invalid latitude';
      case 'long':
        return parseFloat(value) >= -180 && parseFloat(value) <= 180
          ? ''
          : 'Invalid longitude';
      case 'losses':
        return parseFloat(value) >= 0 && parseFloat(value) <= 100
          ? ''
          : 'Losses must be between 0-100%';
      case 'tilt':
        return parseFloat(value) >= 0 && parseFloat(value) <= 30
          ? ''
          : 'Tilt must be between 0-30°';
      case 'inv_eff':
        return parseFloat(value) >= 90 && parseFloat(value) <= 100
          ? ''
          : 'Efficiency must be between 90-100%';
      case 'pin_code':
        return /^\d{6}$/.test(value) ? '' : 'PIN code must be exactly 6 digits';
      case 'angle_from_north':
        return parseFloat(value) >= 0 && parseFloat(value) <= 360
          ? ''
          : 'Angle must be between 0-360°';
      case 'soil_condition':
        return ['optimistic', 'realistic', 'neglected'].includes(value)
          ? ''
          : 'Invalid soil condition';
      default:
        return '';
    }
  };
  const handleInputChange = (field) => (event) => {
    const value = event.target.value;

    // Handle empty string for number fields
    const processedValue = [
      'system_capacity_in_kw',
      'lat',
      'long',
      'losses',
      'tilt',
      'inv_eff',
      'angle_from_north',
    ].includes(field)
      ? value === ''
        ? ''
        : parseFloat(value)
      : value;

    setFormData((prev) => ({
      ...prev,
      [field]: processedValue,
    }));

    const error = validateField(field, value);
    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));
  };
  const handleSubmit = async () => {
    try {
      // 1. Validate required fields first
      const requiredFields = ['pin_code', 'system_capacity_in_kw', 'tilt'];
      const missingFields = requiredFields.filter((field) => !formData[field]);

      if (missingFields.length > 0) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          missingFields.forEach((field) => {
            newErrors[field] = 'This field is required';
          });
          return newErrors;
        });
        setSnackbar({
          open: true,
          message: `Required fields missing: ${missingFields.join(', ')}`,
          severity: 'error',
        });
        return;
      }

      // 2. Validate all fields
      const newErrors = {};
      Object.keys(formData).forEach((key) => {
        const error = validateField(key, formData[key]);
        if (error) newErrors[key] = error;
      });

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        setSnackbar({
          open: true,
          message: 'Please fix the errors in the form',
          severity: 'error',
        });
        return;
      }

      setLoading(true); // 3. Prepare params object with type conversion
      const params = {
        // Required fields
        pin_code: formData.pin_code.toString(),
        system_capacity_in_kw: parseFloat(formData.system_capacity_in_kw),
        tilt: parseFloat(formData.tilt),
      };

      // Define optional parameters
      const optionalParams = {
        lat:
          formData.lat !== '' && formData.lat != null
            ? parseFloat(formData.lat)
            : null,
        long:
          formData.long !== '' && formData.long != null
            ? parseFloat(formData.long)
            : null,
        losses:
          formData.losses !== '' && formData.losses != null
            ? parseFloat(formData.losses)
            : null,
        soil_condition: formData.soil_condition || null,
        inv_eff:
          formData.inv_eff !== '' && formData.inv_eff != null
            ? parseFloat(formData.inv_eff)
            : null,
        angle_from_north:
          formData.angle_from_north !== '' && formData.angle_from_north != null
            ? parseFloat(formData.angle_from_north)
            : null,
      };

      // Only add non-null optional parameters to the params object
      Object.entries(optionalParams).forEach(([key, value]) => {
        if (value !== null && value !== '' && value !== undefined) {
          params[key] = value;
        }
      });

      const response = await getGenerationData(params);
      if (
        !response ||
        !response.outputs ||
        !response.inputs ||
        !response.station_info
      ) {
        throw new Error('Invalid response format from API');
      }

      setApiData(response);
      setSnackbar({
        open: true,
        message: 'Generation data calculated successfully!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error fetching generation data:', error);
      setSnackbar({
        open: true,
        message:
          error.message === 'Invalid response format from API'
            ? 'Invalid response from server. Please try again.'
            : 'Error calculating generation data. Please check your input and try again.',
        severity: 'error',
      });
      setApiData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initialize form data from the provided data prop
    handleSubmit();
  }, []);

  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const isDataLoading =
    !apiData?.inputs || !apiData?.outputs || !apiData?.station_info;

  const chartData = months.map((month, index) => ({
    month,
    Generation:
      parseFloat(apiData?.outputs.ac_monthly?.[index]?.toFixed(1)) || 0,
    Soiling: parseFloat(apiData?.inputs.soiling?.[index]) || 0,
    Sunlight:
      parseFloat(apiData?.outputs.solrad_monthly?.[index]?.toFixed(2)) || 0,
  }));

  return (
    <>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Box
        component='header'
        sx={isMobile ? mobileHeaderStyle : defaultHeaderStyle}
      >
        <Logo />
      </Box>
      <Box p={2} sx={{ maxWidth: 1024, margin: '0 auto' }}>
        <Paper
          elevation={0}
          sx={{
            mb: 6,
            // p: 3,
            // borderRadius: '9px',
            // border: '1px solid #EBEBEB',
          }}
        >
          <Typography variant='h5' fontWeight='bold' gutterBottom>
            System configuration
          </Typography>

          <Grid container spacing={3} mt={1} maxWidth={700}>
            {/* Non-Advanced Fields */}
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label='Pin code'
                value={formData.pin_code}
                onChange={handleInputChange('pin_code')}
                error={!!errors.pin_code}
                helperText={errors.pin_code}
                type='number'
                inputProps={{ step: 1 }}
                variant='outlined'
                size='small'
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label='Plant size (kW)'
                value={formData.system_capacity_in_kw}
                onChange={handleInputChange('system_capacity_in_kw')}
                error={!!errors.system_capacity_in_kw}
                helperText={errors.system_capacity_in_kw}
                type='number'
                inputProps={{ step: 1 }}
                variant='outlined'
                size='small'
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label='Tilt angle (degrees)'
                value={formData.tilt}
                onChange={handleInputChange('tilt')}
                error={!!errors.tilt}
                helperText={errors.tilt}
                type='number'
                inputProps={{ min: 0, max: 90, step: 0.1 }}
                variant='outlined'
                size='small'
              />
            </Grid>

            {/* Advanced Options Toggle (Minimal Accordion) */}
            <Grid item xs={12} style={{ padding: '0px ', paddingLeft: '24px' }}>
              <Accordion
                expanded={showAdvanced}
                onChange={() => setShowAdvanced(!showAdvanced)}
                disableGutters
                elevation={0}
                square
              >
                <AccordionSummary sx={{ p: '0px', bgcolor: 'transparent' }}>
                  <Typography
                    sx={{
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    {showAdvanced ? 'Hide advanced' : 'Advanced'}{' '}
                    {showAdvanced ? (
                      <RemoveCircleOutlineIcon fontSize='small' />
                    ) : (
                      <ControlPointIcon fontSize='small' />
                    )}{' '}
                  </Typography>{' '}
                </AccordionSummary>
                <AccordionDetails sx={{ p: '10px 0px' }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        disabled={isSelectedPINCode}
                        label='Latitude'
                        value={formData.lat}
                        onChange={handleInputChange('lat')}
                        error={!!errors.lat}
                        helperText={errors.lat}
                        type='number'
                        inputProps={{ step: 0.000001 }}
                        variant='outlined'
                        size='small'
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        disabled={isSelectedPINCode}
                        label='Longitude'
                        value={formData.long}
                        onChange={handleInputChange('long')}
                        error={!!errors.long}
                        helperText={errors.long}
                        type='number'
                        inputProps={{ step: 0.000001 }}
                        variant='outlined'
                        size='small'
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label='Inverter efficiency (%)'
                        value={formData.inv_eff}
                        onChange={handleInputChange('inv_eff')}
                        error={!!errors.inv_eff}
                        helperText={errors.inv_eff}
                        type='number'
                        inputProps={{ min: 90, max: 100, step: 0.1 }}
                        variant='outlined'
                        size='small'
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        select
                        label='Soil/dust condition'
                        value={formData.soil_condition}
                        onChange={handleInputChange('soil_condition')}
                        variant='outlined'
                        size='small'
                        SelectProps={{ native: true }}
                      >
                        <option value='' disabled selected hidden></option>
                        <option value='optimistic'>Optimistic</option>
                        <option value='realistic'>Realistic</option>
                        <option value='neglected'>Neglected</option>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label='Losses (%)'
                        value={formData.losses}
                        onChange={handleInputChange('losses')}
                        error={!!errors.losses}
                        helperText={errors.losses}
                        type='number'
                        inputProps={{ min: 0, max: 100, step: 0.1 }}
                        variant='outlined'
                        size='small'
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label='Angle from North (degrees)'
                        value={formData.angle_from_north}
                        onChange={handleInputChange('angle_from_north')}
                        error={!!errors.angle_from_north}
                        helperText={errors.angle_from_north}
                        type='number'
                        inputProps={{ min: 0, max: 360, step: 1 }}
                        variant='outlined'
                        size='small'
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
          <Stack spacing={2} direction={isMobile ? 'column' : 'row'} mt={4}>
            <Button
              variant='contained'
              onClick={handleSubmit}
              disabled={loading}
              sx={{
                borderRadius: '32px',
                backgroundColor: '#A8D5A8',
                color: '#000',
                fontWeight: 'bold',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#91c291',
                },
              }}
            >
              {loading ? 'Calculating...' : 'Estimate generation'}
            </Button>
          </Stack>
        </Paper>

        <Divider sx={{ my: 4 }} />
        <Paper
          display={loading ? 'none' : 'block'}
          elevation={0}
          // sx={{ p: 3, borderRadius: '9px', border: '1px solid #EBEBEB' }}
        >
          {' '}
          {isDataLoading ? (
            <>
              <Skeleton variant='text' width={300} height={80} sx={{ mb: 1 }} />
              <Skeleton variant='text' width={150} height={32} sx={{ mb: 4 }} />
            </>
          ) : (
            <>
              <Typography
                variant='h2'
                color='primary'
                sx={{
                  mb: 1,
                  fontFamily: 'Plus Jakarta Sans',
                  fontWeight: 700,
                  letterSpacing: '-0.5px',
                  WebkitTextStroke: isMobile ? '0.6px black' : '1.0px black',
                  color: '#000',
                }}
              >
                {apiData?.outputs?.ac_annual != null
                  ? Math.round(apiData.outputs.ac_annual).toLocaleString(
                      'en-IN',
                    )
                  : ''}{' '}
                units
              </Typography>
              <Typography variant='h6' fontWeight='bold'>
                Annual production
              </Typography>
            </>
          )}
          <Typography variant='h5' fontWeight='600' mt={4} mb={2}>
            Month-wise estimates
          </Typography>{' '}
          {isDataLoading ? (
            <>
              <Skeleton
                variant='rectangular'
                width={300}
                height={40}
                sx={{ mb: 3, borderRadius: '10px' }}
              />
              <Skeleton
                variant='rectangular'
                width='100%'
                height={300}
                sx={{ borderRadius: '10px' }}
              />
            </>
          ) : (
            <>
              <ToggleButtonGroup
                size='small'
                value={view}
                defaultValue='table'
                exclusive
                onChange={(event, newValue) => {
                  setView(newValue || 'table');
                }}
                sx={{
                  borderRadius: '10px',
                  overflow: 'hidden',
                  border: '1px solid #ccc',
                  width: '300px',
                }}
              >
                {[
                  {
                    value: 'graph',
                    label: 'Graph',
                    icon: (
                      <BarChartIcon
                        sx={{ fontSize: '22px', paddingRight: '5px' }}
                      />
                    ),
                  },
                  {
                    value: 'table',
                    label: 'Table',
                    icon: (
                      <TableChartIcon
                        sx={{ fontSize: '22px', paddingRight: '5px' }}
                      />
                    ),
                  },
                ].map(({ value, label, icon }) => (
                  <ToggleButton
                    key={value}
                    value={value}
                    sx={{
                      flex: 1,
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 0,
                      backgroundColor:
                        view === value ? theme.palette.primary.main : 'white',
                      color:
                        view === value ? 'white' : theme.palette.primary.main,
                      '&.Mui-selected': {
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        '&:hover': { backgroundColor: 'darkgreen' },
                      },
                    }}
                  >
                    {icon}
                    {label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>

              {view === 'table' ? (
                <MWETable
                  months={months}
                  outputs={apiData?.outputs}
                  inputs={apiData?.inputs}
                />
              ) : (
                <MWEGraph chartData={chartData} />
              )}
            </>
          )}{' '}
          <Typography variant='h5' fontWeight='bold' mt={4} mb={2}>
            Weather data
          </Typography>
          {isDataLoading ? (
            <>
              <Skeleton variant='text' width={250} height={24} sx={{ mb: 1 }} />
              <Skeleton variant='text' width={200} height={24} sx={{ mb: 1 }} />
              <Skeleton variant='text' width={180} height={24} sx={{ mb: 1 }} />
              <Skeleton variant='text' width={180} height={24} />
            </>
          ) : (
            <>
              <Typography variant='body2'>
                Source: {apiData?.station_info.weather_data_source || 'N/A'}
              </Typography>
              <Typography variant='body2'>
                Distance from weather station:{' '}
                {(apiData?.station_info.distance / 1000).toFixed(2)} km
              </Typography>
              <Typography variant='body2'>
                Lattitude: {apiData?.station_info.lat}
              </Typography>
              <Typography variant='body2'>
                Longitude: {apiData?.station_info.lon}
              </Typography>
            </>
          )}
        </Paper>
      </Box>
    </>
  );
};

export default SolarEstimationUI;
